/**
 * cart-promo-sync.js
 *
 * Cart section refresh + counter sync 모듈 (Phase K, 2026-05-27).
 *
 * 책임:
 *   - Shopify section reload API 로 cart-items HTML 재가져오기 + innerHTML swap
 *   - 헤더 + 드로어 카트 카운트 동기화 (global updateCartCounters 재사용)
 *   - cart:updated 이벤트 dispatch
 *   - swap 직후 BXGY gift marking 동기 호출 (race 차단)
 *
 * 의존: hub instance — applyBxgyGiftMarking, renderAll 호출
 *
 * 로드 순서:
 *   1. cart-coupon-utils.js
 *   2. cart-promo-effects.js
 *   3. cart-promo-storefront.js
 *   4. cart-promo-bxgy.js
 *   5. cart-promo-cascade.js
 *   6. cart-promo-sync.js  ← 본 파일
 *   7. cart-coupon-hub.js
 *
 * 회귀 박제:
 *   - BXGY confirm 직후 cart-item__actions--mobile 데스크탑 노출 (2026-05-24)
 *     swap 직후 동기 applyBxgyGiftMarking + renderAll 마지막 1회.
 *   - sections fetch stale (2026-05-25) — cache: 'no-store' + cache buster `_t`
 *   - 헤더/드로어 카운트 stale (2026-05-27) — updateCartCounters 재사용 (cart-stock-guard 와 동일 패턴)
 */
(function (global) {
  'use strict';

  const Sync = {

    async refreshCartSection(hub) {
      // discount code 적용/제거 후 cart section 재렌더 — total / discount / line 갱신.
      // Shopify section reload API — 단일 cart-items section 다시 render.
      //
      // 🔴 [중요] 회귀 case (2026-05-24): BXGY confirm 직후 cart-item__actions--mobile 이 데스크탑에서도 노출
      //   원인 가설: wrap.innerHTML swap → 새 <coupon-hub> 인스턴스 생성 + 기존 인스턴스 detached.
      //   기존 인스턴스의 renderAll() 이 stale 상태로 동작.
      //   동시에 새 인스턴스의 loadInitialData 가 async 로 돌며 race condition.
      //   GY 라인이 cart-item--gift 마킹 안 된 채 노출 → SSR 의 qty+remove UI 보임.
      //   회귀 case + 박제: docs/policy/cart-coupon-hub.md §회귀 함정 (BXGY confirm CSS 깨짐)
      //
      //   fix:
      //   (1) CSS 가 gift 라인에서 qty/remove SSR UI 강제 hide (cart-coupon-hub.css §6-B)
      //   (2) swap 직후 가장 빠른 micro-task 에서 applyBxgyGiftMarking 동기 호출 (race 차단)
      //   (3) renderAll 은 마지막에 한 번 호출 (이중 호출 race 차단)
      const sectionId = document.getElementById('main-cart-items')?.dataset.id;
      if (!sectionId) {
        hub?.renderAll();
        return;
      }
      try {
        // 🔴 cache: 'no-store' 필수 — Shopify section API 가 cart 캐시해서 stale HTML 반환 가능.
        // 회귀: cart/add.js 직후 sections fetch 가 옛 cart 의 HTML 줘서 새 라인 안 보이고 promo-locked/gift 클래스 미적용.
        // 🔴 [중요] base 는 /cart (루트 / 아님). main-cart 의 breadcrumb 이 <cart-items> 안에 있어 swap 대상에 포함되는데,
        //    /?sections= (루트) 로 render 하면 template.name='index' → breadcrumb 이 "Home/Home" 으로 와서 화면 덮어씀.
        //    sections_url 로 컨텍스트 경로도 명시 (다른 cart 모듈과 동일 패턴). 회귀 case: docs 카트 breadcrumb Home/Home.
        const res = await fetch(MISTO.localeUrl(`/cart?sections=${sectionId},cart-counter&sections_url=${encodeURIComponent(window.location.pathname)}&_t=${Date.now()}`), {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        const data = await res.json();
        const wrap = document.querySelector(`#shopify-section-${sectionId} cart-items`);
        if (wrap && data[sectionId]) {
          const newWrap = new DOMParser()
            .parseFromString(data[sectionId], 'text/html')
            .querySelector('cart-items');
          if (newWrap) {
            wrap.innerHTML = newWrap.innerHTML;
            // 🔴 swap 직후 동기 gift marking — SSR `data-applied-promotion-type` 만으로 판정 가능 (state 불필요).
            // 새 <coupon-hub> 의 loadInitialData async 완료 전이지만 BXGY gift CSS 부착은 즉시 필요.
            // 안 하면 GY 라인의 qty+remove SSR UI 가 잠시 노출되는 flash 발생.
            hub?.applyBxgyGiftMarking();
          }
        }
        // 🔴 헤더 + 드로어 카트 카운트 갱신 (2026-05-27).
        //   회귀: refreshCartSection 이 cart-items section 만 swap → header.liquid 의 .cart-count-badge 와
        //   cart-drawer.liquid 의 .cart-drawer__title-counter 가 옛 값 유지 → 자식 GY 삭제 시 카운트 stale.
        //   fix: assets/global.js 의 updateCartCounters() 재사용 (cart-stock-guard.js 도 같은 패턴).
        //   /cart.js 1회 fetch 로 헤더 + 드로어 둘 다 동기화 + zero 상태 hidden 클래스 토글.
        if (typeof global.updateCartCounters === 'function') {
          await global.updateCartCounters();
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (err) {
        console.warn('[cart-promo-sync] refreshCartSection', err);
      }
      hub?.renderAll();
    },
  };

  global.CartPromoSync = Sync;
})(typeof window !== 'undefined' ? window : globalThis);
